import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/utils/cn'
import { AcceptAlertDialog, type AcceptAlertDialogProps } from './AcceptAlertDialog'
import { AlertDetail, type AlertDetailProps } from './AlertDetail'
import { AlertInbox, type AlertInboxProps } from './AlertInbox'
import { InboxFilters, type InboxFiltersProps } from './InboxFilters'
import type { RuleRow } from './RulesList'

/** The inbox, its filters and the dialog that accepts an alert into an investigation. */
export interface MonitorInboxPane extends AlertInboxProps {
  filters?: InboxFiltersProps
  accept?: AcceptAlertDialogProps
  truncatedAt?: number | null
}

interface InboxPaneProps {
  inbox: MonitorInboxPane
  detail: { rules: readonly RuleRow[] | null; onOpenRule: (ruleId: string) => void }
  selection: { key: string | null; onSelect: (key: string | null) => void }
}

/** Account for the space left by the navigation and assistant, not just the viewport. */
function useInlineDetail() {
  const ref = useRef<HTMLDivElement>(null)
  const [inline, setInline] = useState<boolean | null>(null)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => setInline(entry.contentRect.width >= 960))
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  return { ref, inline }
}

function DetailPane({ detail, inline }: { detail: AlertDetailProps; inline: boolean }) {
  const { t } = useTranslation()
  const trigger = useRef<HTMLElement | null>(null)
  if (inline) {
    return (
      <Card asChild className="sticky top-0 max-h-[calc(100dvh-14rem)] overflow-y-auto py-0">
        <aside aria-label={t('cases.monitor.detail.title')}>
          <AlertDetail {...detail} />
        </aside>
      </Card>
    )
  }
  return (
    <Sheet open onOpenChange={open => !open && detail.actions.onClose()}>
      <SheetContent
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={() => {
          trigger.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null
        }}
        onCloseAutoFocus={event => {
          event.preventDefault()
          trigger.current?.focus({ preventScroll: true })
        }}
        className="overflow-y-auto bg-card motion-reduce:animate-none motion-reduce:transition-none sm:max-w-lg"
      >
        <SheetTitle className="sr-only">{t('cases.monitor.detail.title')}</SheetTitle>
        <AlertDetail {...detail} />
      </SheetContent>
    </Sheet>
  )
}

interface InboxContentProps extends Pick<InboxPaneProps, 'inbox' | 'selection'> {
  detail: AlertDetailProps | null
  inline: boolean | null
}

function InboxContent({ inbox, selection, detail, inline }: InboxContentProps) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'grid grid-cols-1 items-start gap-4',
        detail && inline && 'grid-cols-[minmax(0,3fr)_minmax(0,2fr)]'
      )}
    >
      <div className="flex min-w-0 flex-col gap-3">
        <AlertInbox {...inbox} selection={selection} />
        {inbox.truncatedAt ? (
          <p className="text-xs text-muted-foreground">
            {t('cases.monitor.inbox.truncated', { count: inbox.truncatedAt })}
          </p>
        ) : null}
      </div>
      {detail && inline !== null ? <DetailPane detail={detail} inline={inline} /> : null}
    </div>
  )
}

export function InboxPane({ inbox, detail, selection }: InboxPaneProps) {
  const { ref, inline } = useInlineDetail()
  const groups = inbox.view.status === 'ready' ? inbox.view.rows : []
  const group = groups.find(row => row.key === selection.key)
  const close = () => {
    ref.current
      ?.querySelector<HTMLButtonElement>('[aria-current="true"] [data-alert-detail-trigger]')
      ?.focus({ preventScroll: true })
    selection.onSelect(null)
  }
  const selectedDetail: AlertDetailProps | null = group
    ? {
        group,
        rule:
          detail.rules && (detail.rules.find(rule => rule.id === group.alerts[0].ruleId) ?? null),
        triage: inbox.triage,
        actions: {
          onClose: close,
          onOpenDocument: inbox.onOpenDocument,
          onOpenRule: detail.onOpenRule,
        },
      }
    : null
  return (
    <div
      ref={ref}
      className="flex min-w-0 flex-col gap-4"
      onKeyDown={event => {
        if (event.key === 'Escape' && !event.defaultPrevented && inline && group) close()
      }}
    >
      {inbox.filters ? <InboxFilters {...inbox.filters} /> : null}
      <InboxContent
        inbox={inbox}
        selection={{ ...selection, key: group?.key ?? null }}
        detail={selectedDetail}
        inline={inline}
      />
      {inbox.accept ? <AcceptAlertDialog {...inbox.accept} /> : null}
    </div>
  )
}
